<script lang="ts">
import { defineComponent } from 'vue';
import { formatDate } from '@/utils/format';
import IconSports from '@/helpers/icon-pack-sports.vue';
import { getIconAlias } from '@/utils/transform';
import ClipboardIcon from '@/components/clipboard-icon.vue';

export default defineComponent({
  name: 'pool-details',
  components: { ClipboardIcon, IconSports },
  computed: {
    eventsSorted() {
      return this.pool.poolEvents.toSorted((a, b) =>
        a.market.Fixture.StartTimeUtc > b.market.Fixture.EndTimeUtc ? 1 : -1
      );
    },
  },
  methods: {
    getIconAlias,
    formatDate,
    deleteMarket(marketId: number) {
      this.$emit('deleteMarket', marketId);
    },
  },
  emits: ['deleteMarket'],
  props: ['pool'],
});
</script>

<template>
  <table class="basic-table" v-if="pool">
    <thead>
      <tr>
        <th>#</th>
        <th style="width: 35px"></th>
        <th>
          <i class="pi pi-trophy pt-1" />
        </th>
        <th class="!text-left">Матч</th>
        <th class="!text-left">Турнир</th>
        <th>Регион</th>
        <th>Начало</th>
        <th>Маркет</th>
        <th>Исходы</th>
        <th><i class="pi pi-cog" /></th>
      </tr>
    </thead>
    <tbody>
      <tr
        v-for="(event, i) of eventsSorted"
        :key="event.id"
        :class="{
          finished: event.market.Selections.some((s) => s.ResultStatus) && !event.voided,
          cancelled: event.voided,
        }"
      >
        <td style="width: 20px">{{ i + 1 }}</td>
        <td>
          <IconSports :id="getIconAlias(event.market.Fixture.Sport.Id)" :height="20" :width="20" />
        </td>
        <td>
          <i
            :class="{
              pi: true,
              'pi-stopwatch text-gray-500':
                !event.market.Selections.some((s) => s.ResultStatus) && !event.voided,
              'pi-check text-green-500':
                event.market.Selections.some((s) => s.ResultStatus) && !event.voided,
              'pi-times text-red-500 pt-1': event.voided,
            }"
          />
        </td>
        <td class="!text-left">
          <span
            class="underline cursor-pointer text-blue-500"
            @click.stop="
              $router.push({ name: 'detailed-event', params: { id: event.market.Fixture.Id } })
            "
          >
            {{ event.market.Fixture.Name }}
          </span>
          <br />
          <span class="text-gray-500">{{ event.market.Fixture.Id }}</span>
          <clipboard-icon :text="event.market.Fixture.Id" class="mx-1" iconClass="text-xs" />
          <clipboard-icon
            :text="event.market.Fixture.Name"
            iconClass="text-xs"
            icon="pi-file-word"
          />
        </td>
        <td class="!text-left">{{ event.market.Fixture.Competition.Name }}</td>
        <td class="!text-left">{{ event.market.Fixture.Competition.Region.Name }}</td>
        <td class="text-nowrap">
          {{ formatDate(event.market.Fixture.StartTimeUtc, 'DD.MM.YYYY') }}
          <span class="text-blue-500 font-bold">{{
            formatDate(event.market.Fixture.StartTimeUtc, 'HH:mm')
          }}</span>
        </td>
        <td>
          <div>{{ event.market.Name }}</div>
          <span class="text-gray-500 mr-1">{{ event.market.Id }}</span>
          <span
            :class="{
              'text-green-500': event.market.TradingStatus === 'Open',
              'text-red-500': event.market.TradingStatus === 'Closed',
              'text-blue-500': event.market.TradingStatus === 'Suspended',
            }"
          >
            {{ event.market.TradingStatus }}
          </span>
        </td>
        <td>
          <div class="flex gap-1">
            <div
              class="border rounded grid grid-cols-[max-content_max-content] gap-2"
              v-for="selection in event.market.Selections"
              :key="selection.Id"
            >
              <div class="text-left p-2 pb-0">{{ selection.Name }}</div>
              <div class="row-span-2 place-content-center p-2">
                <i
                  :class="{
                    pi: true,
                    'pi-check text-green-500': selection.ResultStatus === 'Winner',
                    'pi-times text-red-500': selection.ResultStatus === 'Loser',
                    'pi-stopwatch text-gray-500': !selection.ResultStatus,
                    'pi-percentage text-green-500':
                      (selection.ResultStatus === 'Partial' &&
                        selection.ResultStatus?.AdditionalResultData.PercentageWin > 0) ||
                      selection.ResultStatus === 'Placed',
                    'pi-percentage text-red-500':
                      selection.ResultStatus === 'Partial' &&
                      selection.ResultStatus?.AdditionalResultData.PercentageWin <= 0,
                    'pi-refresh text-yellow-500': selection.ResultStatus === 'Pushed',
                    'pi-exclamation text-red-500': selection.ResultStatus === 'None',
                  }"
                />
              </div>
              <div class="text-left p-2 pt-0">
                <span class="text-gray-500 mr-1">
                  {{ selection.Id }}
                  <clipboard-icon :text="selection.Id" icon-class="text-xs" />
                </span>
                <span
                  :class="{
                    'mr-1': true,
                    'text-green-500': selection.TradingStatus === 'Trading',
                    'text-blue-500': event.market.TradingStatus === 'Suspended',
                    'text-red-500': ['Unpriced', 'NonRunner'].includes(event.market.TradingStatus),
                  }"
                >
                  {{ selection.TradingStatus }}
                </span>
                <span class="text-green-500 font-bold">x{{ selection.Decimal }}</span>
              </div>
            </div>
          </div>
        </td>
        <td>
          <i
            class="pi pi-trash text-red-500 p-2 cursor-pointer"
            @click="deleteMarket(event.marketId)"
          />
        </td>
      </tr>
      <tr v-if="!eventsSorted.length">
        <td colspan="1000">События еще не добавлены</td>
      </tr>
    </tbody>
  </table>
</template>

<style scoped lang="scss">
.finished {
  @apply bg-opacity-10 bg-green-500;
}

.cancelled {
  @apply bg-opacity-10 bg-red-500;
}
</style>
